import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { ErrorBoundary } from "@/components/common/ErrorBoundary";
import Index from "./pages/Index";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import ForgotPassword from "./pages/ForgotPassword";
import Dashboard from "./pages/Dashboard";
import Categories from "./pages/Categories";
import Transactions from "./pages/Transactions";
import AddTransactions from "./pages/AddTransactions";
import Goals from "./pages/Goals";
import NotFound from "./pages/NotFound";
import { DASHBOARD_SECTIONS, ROUTES } from "@/lib/constants";

const queryClient = new QueryClient();

// Análise and Orçamento are two sections of Painel now. Their old paths — /analysis, /budget and the Portuguese
// /analise from before the routes went English — send bookmarks there: /budget to the Orçamento section, the
// others to the top of the page, where the cash flow starts. The period filters sit in the query string, so they
// are kept. Not wrapped in ProtectedRoute: the destination already is.
const DashboardRedirect = ({ hash }: { hash?: string }) => {
  const { search } = useLocation();
  return <Navigate to={{ pathname: ROUTES.dashboard, search, hash }} replace />;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider>
      <AuthProvider>
        <TooltipProvider>
          <ErrorBoundary>
            <Toaster />
            <Sonner />
            <BrowserRouter>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/transactions"
                element={
                  <ProtectedRoute>
                    <Transactions />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/transactions/add"
                element={
                  <ProtectedRoute>
                    <AddTransactions />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/categories"
                element={
                  <ProtectedRoute>
                    <Categories />
                  </ProtectedRoute>
                }
              />
              <Route path="/analysis" element={<DashboardRedirect />} />
              <Route path="/analise" element={<DashboardRedirect />} />
              <Route path="/budget" element={<DashboardRedirect hash={DASHBOARD_SECTIONS.budget} />} />
              <Route
                path="/goals"
                element={
                  <ProtectedRoute>
                    <Goals />
                  </ProtectedRoute>
                }
              />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </BrowserRouter>
          </ErrorBoundary>
        </TooltipProvider>
      </AuthProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
